// ===========================================
// TIPOS: Sistema de Auditoría (CORE)
// ===========================================

// Acciones auditables
export type AccionAuditoria =
  | 'crear'
  | 'editar'
  | 'eliminar'
  | 'ver'
  | 'login'
  | 'logout'
  | 'cambiar_estado'
  | 'generar_lote'
  | 'subir'
  | 'descargar'
  | 'cambiar_rol'
  | 'activar'
  | 'desactivar'
  | 'exportar'
  | 'vincular'
  | 'desvincular'
  | 'emitir'
  | 'anular'
  | 'importar'
  | 'facturar'
  | 'descartar'
  | 'restaurar'

// Entidades auditables
export type EntidadAuditoria =
  | 'cliente'
  | 'persona'
  | 'movimiento'
  | 'presupuesto'
  | 'tarea'
  | 'documento'
  | 'ddjj'
  | 'empleado'
  | 'vencimiento'
  | 'evento'
  | 'expediente'
  | 'configuracion'
  | 'usuario'
  | 'sistema'
  | 'comprobante'
  | 'punto_venta'
  | 'impuesto'
  | 'liquidacion'
  | 'obligacion_recurrente'
  | 'concepto_responsable'

// Registro de auditoría con campos de contexto
export interface AuditLog {
  id: string
  user_email: string
  accion: AccionAuditoria
  entidad: EntidadAuditoria
  entidad_id: string | null
  detalle: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  workspace: string | null
  pantalla: string | null
  organizacion_id: string | null
  created_at: string
}

// Vista de auditoría con datos de usuario
export interface AuditLogConUsuario extends AuditLog {
  user_name: string | null
  user_rol: string | null
}

// Datos para crear log de auditoría
export interface AuditLogInsert {
  user_email: string
  accion: AccionAuditoria
  entidad: EntidadAuditoria
  entidad_id?: string | null
  detalle?: Record<string, unknown> | null
  ip_address?: string | null
  user_agent?: string | null
  workspace?: string | null
  pantalla?: string | null
  organizacion_id?: string | null
}

// Filtros para consulta de auditoría
export interface FiltrosAuditoria {
  user_email?: string
  accion?: AccionAuditoria
  entidad?: EntidadAuditoria
  entidad_id?: string
  fecha_desde?: string
  fecha_hasta?: string
  busqueda?: string
  workspace?: string
  organizacion_id?: string
}

// Estadísticas de auditoría
export interface EstadisticasAuditoria {
  totalRegistros: number
  porAccion: Record<string, number>
  porEntidad: Record<string, number>
  porWorkspace: Record<string, number>
  usuariosActivos: number
}

// ===========================================
// Configuración Visual
// ===========================================

export const ACCION_CONFIG: Record<AccionAuditoria, { label: string; color: string; bgClass: string; icon: string }> = {
  crear: {
    label: 'Crear',
    color: '#10B981',
    bgClass: 'bg-green-100 text-green-800',
    icon: 'Plus',
  },
  editar: {
    label: 'Editar',
    color: '#3B82F6',
    bgClass: 'bg-blue-100 text-blue-800',
    icon: 'Pencil',
  },
  eliminar: {
    label: 'Eliminar',
    color: '#EF4444',
    bgClass: 'bg-red-100 text-red-800',
    icon: 'Trash2',
  },
  ver: {
    label: 'Ver',
    color: '#6B7280',
    bgClass: 'bg-gray-100 text-gray-800',
    icon: 'Eye',
  },
  login: {
    label: 'Iniciar sesión',
    color: '#8B5CF6',
    bgClass: 'bg-purple-100 text-purple-800',
    icon: 'LogIn',
  },
  logout: {
    label: 'Cerrar sesión',
    color: '#8B5CF6',
    bgClass: 'bg-purple-100 text-purple-800',
    icon: 'LogOut',
  },
  cambiar_estado: {
    label: 'Cambiar estado',
    color: '#F59E0B',
    bgClass: 'bg-amber-100 text-amber-800',
    icon: 'RefreshCw',
  },
  descartar: {
    label: 'Descartar',
    color: '#F97316',
    bgClass: 'bg-orange-100 text-orange-800',
    icon: 'Archive',
  },
  restaurar: {
    label: 'Restituir',
    color: '#10B981',
    bgClass: 'bg-emerald-100 text-emerald-800',
    icon: 'ArchiveRestore',
  },
  generar_lote: {
    label: 'Generar lote',
    color: '#06B6D4',
    bgClass: 'bg-cyan-100 text-cyan-800',
    icon: 'Layers',
  },
  subir: {
    label: 'Subir archivo',
    color: '#14B8A6',
    bgClass: 'bg-teal-100 text-teal-800',
    icon: 'Upload',
  },
  descargar: {
    label: 'Descargar',
    color: '#64748B',
    bgClass: 'bg-slate-100 text-slate-800',
    icon: 'Download',
  },
  cambiar_rol: {
    label: 'Cambiar rol',
    color: '#EC4899',
    bgClass: 'bg-pink-100 text-pink-800',
    icon: 'UserCog',
  },
  activar: {
    label: 'Activar',
    color: '#22C55E',
    bgClass: 'bg-green-100 text-green-800',
    icon: 'CheckCircle',
  },
  desactivar: {
    label: 'Desactivar',
    color: '#EF4444',
    bgClass: 'bg-red-100 text-red-800',
    icon: 'XCircle',
  },
  exportar: {
    label: 'Exportar',
    color: '#0EA5E9',
    bgClass: 'bg-sky-100 text-sky-800',
    icon: 'FileDown',
  },
  vincular: {
    label: 'Vincular',
    color: '#10B981',
    bgClass: 'bg-green-100 text-green-800',
    icon: 'Link',
  },
  desvincular: {
    label: 'Desvincular',
    color: '#F59E0B',
    bgClass: 'bg-amber-100 text-amber-800',
    icon: 'Unlink',
  },
  emitir: {
    label: 'Emitir',
    color: '#10B981',
    bgClass: 'bg-green-100 text-green-800',
    icon: 'Send',
  },
  anular: {
    label: 'Anular',
    color: '#EF4444',
    bgClass: 'bg-red-100 text-red-800',
    icon: 'XCircle',
  },
  importar: {
    label: 'Importar',
    color: '#8B5CF6',
    bgClass: 'bg-purple-100 text-purple-800',
    icon: 'Upload',
  },
  facturar: {
    label: 'Facturar',
    color: '#0EA5E9',
    bgClass: 'bg-sky-100 text-sky-800',
    icon: 'Receipt',
  },
}

export const ENTIDAD_CONFIG: Record<EntidadAuditoria, { label: string; labelPlural: string; icon: string }> = {
  cliente: {
    label: 'Cliente',
    labelPlural: 'Clientes',
    icon: 'Users',
  },
  persona: {
    label: 'Persona',
    labelPlural: 'Personas',
    icon: 'User',
  },
  movimiento: {
    label: 'Movimiento',
    labelPlural: 'Movimientos',
    icon: 'Receipt',
  },
  presupuesto: {
    label: 'Presupuesto',
    labelPlural: 'Presupuestos',
    icon: 'FileText',
  },
  tarea: {
    label: 'Tarea',
    labelPlural: 'Tareas',
    icon: 'CheckSquare',
  },
  documento: {
    label: 'Documento',
    labelPlural: 'Documentos',
    icon: 'FolderOpen',
  },
  ddjj: {
    label: 'DDJJ',
    labelPlural: 'Declaraciones Juradas',
    icon: 'FileCheck',
  },
  empleado: {
    label: 'Empleado',
    labelPlural: 'Empleados',
    icon: 'UserCog',
  },
  vencimiento: {
    label: 'Vencimiento',
    labelPlural: 'Vencimientos',
    icon: 'Calendar',
  },
  evento: {
    label: 'Evento',
    labelPlural: 'Eventos',
    icon: 'CalendarDays',
  },
  expediente: {
    label: 'Expediente',
    labelPlural: 'Expedientes',
    icon: 'Briefcase',
  },
  configuracion: {
    label: 'Configuración',
    labelPlural: 'Configuraciones',
    icon: 'Settings',
  },
  usuario: {
    label: 'Usuario',
    labelPlural: 'Usuarios',
    icon: 'User',
  },
  sistema: {
    label: 'Sistema',
    labelPlural: 'Sistema',
    icon: 'Monitor',
  },
  comprobante: {
    label: 'Comprobante',
    labelPlural: 'Comprobantes',
    icon: 'FileSpreadsheet',
  },
  punto_venta: {
    label: 'Punto de Venta',
    labelPlural: 'Puntos de Venta',
    icon: 'Building2',
  },
  impuesto: {
    label: 'Impuesto',
    labelPlural: 'Impuestos',
    icon: 'DollarSign',
  },
  liquidacion: {
    label: 'Liquidación',
    labelPlural: 'Liquidaciones',
    icon: 'Calculator',
  },
  obligacion_recurrente: {
    label: 'Obligación recurrente',
    labelPlural: 'Obligaciones recurrentes',
    icon: 'RefreshCw',
  },
  concepto_responsable: {
    label: 'Reparto de concepto',
    labelPlural: 'Repartos de conceptos',
    icon: 'User',
  },
}

// ===========================================
// Opciones para filtros
// ===========================================

export const ACCIONES_OPTIONS = Object.entries(ACCION_CONFIG).map(([value, config]) => ({
  value: value as AccionAuditoria,
  label: config.label,
}))

export const ENTIDADES_OPTIONS = Object.entries(ENTIDAD_CONFIG).map(([value, config]) => ({
  value: value as EntidadAuditoria,
  label: config.label,
}))

// ===========================================
// Accessors
// ===========================================

// `accion` y `entidad` son columnas de texto que también escriben triggers SQL, así que las
// uniones de arriba NO pueden garantizar cobertura: el front se entera de un valor nuevo cuando
// ya está en la base. Sin estos accessors el lookup devuelve undefined y el `.map` de la lista
// tumba la pantalla entera de auditoría — pasó en PROD el 18/08 con `concepto_responsable`.
// El fallback muestra el valor crudo: en una pantalla de auditoría, ver `entidad_rara` es más
// útil que un guion, y deja pista de qué falta agregar acá.

export function getAccionConfig(accion: string) {
  return (
    ACCION_CONFIG[accion as AccionAuditoria] ?? {
      label: accion,
      color: '#6B7280',
      bgClass: 'bg-gray-100 text-gray-800',
      icon: 'HelpCircle',
    }
  )
}

export function getEntidadConfig(entidad: string) {
  return (
    ENTIDAD_CONFIG[entidad as EntidadAuditoria] ?? {
      label: entidad,
      labelPlural: entidad,
      icon: 'HelpCircle',
    }
  )
}

// ===========================================
// Helpers
// ===========================================

export function formatearDetalleAuditoria(detalle: Record<string, unknown> | null): string {
  if (!detalle) return '-'

  const partes: string[] = []

  if (detalle.nombre) partes.push(`"${detalle.nombre}"`)
  if (detalle.titulo) partes.push(`"${detalle.titulo}"`)
  if (detalle.concepto) partes.push(`"${detalle.concepto}"`)
  if (detalle.monto) partes.push(`$${Number(detalle.monto).toLocaleString('es-AR')}`)
  if (detalle.estado_anterior && detalle.estado_nuevo) {
    partes.push(`${detalle.estado_anterior} -> ${detalle.estado_nuevo}`)
  }
  if (detalle.rol_anterior && detalle.rol_nuevo) {
    partes.push(`${detalle.rol_anterior} -> ${detalle.rol_nuevo}`)
  }
  if (detalle.vinculacion) {
    const v = detalle.vinculacion as { tipo: string; id: string }
    partes.push(`vinculado a ${v.tipo}`)
  }

  return partes.length > 0 ? partes.join(' | ') : JSON.stringify(detalle)
}
